import api from './api';
import {
    Resume,
    ResumeData,
    CreateResumeRequest,
    UpdateResumeRequest,
    ResumeTemplate,
    TailoredHistoryItem,
    MessageHistoryItem,
} from '../types';

interface GetResumesResponse {
    resumes: Resume[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export const resumeService = {
    async getResumes(page: number = 1, limit: number = 10): Promise<GetResumesResponse> {
        const response = await api.get<{ success: boolean; data: GetResumesResponse }>(
            `/resumes?page=${page}&limit=${limit}`
        );
        return response.data.data;
    },

    async getResumeById(id: string): Promise<Resume> {
        const response = await api.get<{ success: boolean; data: Resume }>(`/resumes/${id}`);
        return response.data.data;
    },

    async createResume(data: CreateResumeRequest): Promise<Resume> {
        const response = await api.post<{ success: boolean; data: Resume }>('/resumes', data);
        return response.data.data;
    },

    async updateResume(id: string, data: UpdateResumeRequest): Promise<Resume> {
        const response = await api.put<{ success: boolean; data: Resume }>(`/resumes/${id}`, data);
        return response.data.data;
    },

    async deleteResume(id: string): Promise<void> {
        await api.delete(`/resumes/${id}`);
    },

    async shareResume(id: string): Promise<Resume> {
        const response = await api.post<{ success: boolean; data: { resume: Resume } }>(`/resumes/${id}/share`);
        return response.data.data.resume;
    },

    async unshareResume(id: string): Promise<Resume> {
        const response = await api.delete<{ success: boolean; data: { resume: Resume } }>(`/resumes/${id}/share`);
        return response.data.data.resume;
    },

    async getPublicResume(publicId: string): Promise<Resume> {
        const response = await api.get<{ success: boolean; data: Resume }>(`/public/resumes/${publicId}`);
        return response.data.data;
    },

    async getTailoredHistory(limit: number = 20): Promise<TailoredHistoryItem[]> {
        const response = await api.get<{ success: boolean; data: TailoredHistoryItem[] }>(`/tailored?limit=${limit}`);
        return response.data.data;
    },

    async shareTailored(id: string): Promise<TailoredHistoryItem> {
        const response = await api.post<{ success: boolean; data: TailoredHistoryItem }>(`/tailored/${id}/share`);
        return response.data.data;
    },

    async unshareTailored(id: string): Promise<TailoredHistoryItem> {
        const response = await api.delete<{ success: boolean; data: TailoredHistoryItem }>(`/tailored/${id}/share`);
        return response.data.data;
    },

    async getPublicTailored(publicId: string): Promise<TailoredHistoryItem> {
        const response = await api.get<{ success: boolean; data: TailoredHistoryItem }>(`/public/tailored/${publicId}`);
        return response.data.data;
    },

    async getMessageHistory(limit: number = 30): Promise<MessageHistoryItem[]> {
        const response = await api.get<{ success: boolean; data: MessageHistoryItem[] }>(`/messages?limit=${limit}`);
        return response.data.data;
    },

    async exportDocx(id: string): Promise<Blob> {
        const response = await api.get(`/export/docx/${id}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    async exportPdf(id: string): Promise<Blob> {
        const response = await api.get(`/export/pdf/${id}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    async exportDocxFromData(data: ResumeData, title: string = 'tailored-resume'): Promise<Blob> {
        const response = await api.post('/export/docx', {
            data,
            title,
        }, {
            responseType: 'blob',
        });
        return response.data;
    },

    async exportPdfFromData(data: ResumeData, template: ResumeTemplate, title: string = 'tailored-resume'): Promise<Blob> {
        const response = await api.post('/export/pdf', {
            data,
            template,
            title,
        }, {
            responseType: 'blob',
        });
        return response.data;
    },

    async exportCoverLetterPdf(payload: { subject?: string; body: string; title?: string }): Promise<Blob> {
        const response = await api.post('/export/cover-letter', payload, {
            responseType: 'blob',
        });
        return response.data;
    },

    async importResume(
        file: File,
        template: ResumeTemplate = 'ats',
        title?: string
    ): Promise<{ resume: Resume; missingSections: string[] }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('template', template);

        if (title) {
            formData.append('title', title);
        }

        const response = await api.post<{
            success: boolean;
            data: { resume: Resume; missingSections: string[] };
        }>('/resumes/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data.data;
    },
};
